import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import useAuthStore from '../store/authStore';
import { MUNICIPIOS } from '../utils/cidades';
import { Loader2, Search, CheckCircle, AlertCircle } from 'lucide-react';

function apenasNumeros(v) { return (v || '').replace(/\D/g, ''); }

function formatarCNPJ(raw) {
  const n = raw.slice(0, 14);
  if (n.length <= 2)  return n;
  if (n.length <= 5)  return `${n.slice(0,2)}.${n.slice(2)}`;
  if (n.length <= 8)  return `${n.slice(0,2)}.${n.slice(2,5)}.${n.slice(5)}`;
  if (n.length <= 12) return `${n.slice(0,2)}.${n.slice(2,5)}.${n.slice(5,8)}/${n.slice(8)}`;
  return `${n.slice(0,2)}.${n.slice(2,5)}.${n.slice(5,8)}/${n.slice(8,12)}-${n.slice(12)}`;
}

function mapearPorte(desc = '') {
  const d = desc.toUpperCase();
  if (d.includes('MEI'))    return 'mei';
  if (d.includes('MICRO'))  return 'micro';
  if (d.includes('PEQUENA')) return 'pequena';
  if (d.includes('MÉDIA') || d.includes('MEDIA')) return 'media';
  if (d.includes('GRANDE')) return 'grande';
  return '';
}

function mapearCidade(municipio = '') {
  const m = municipio.trim().toUpperCase();
  return MUNICIPIOS.find(c => c.toUpperCase() === m) || '';
}

export default function Cadastro() {
  const [params] = useSearchParams();
  const [tipo, setTipo] = useState(params.get('tipo') || 'candidato');
  const { cadastrarCandidato, cadastrarEmpresa, loading } = useAuthStore();
  const navigate = useNavigate();

  const { register, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm();

  // ── CNPJ state ──────────────────────────────────────────────
  const [cnpjDisplay, setCnpjDisplay] = useState('');
  const [cnpjStatus,  setCnpjStatus]  = useState(null); // null | 'buscando' | 'ok' | 'erro'
  const cnpjAbortRef = useRef(null);

  const buscarCNPJ = useCallback(async (raw) => {
    if (cnpjAbortRef.current) cnpjAbortRef.current.abort();
    const ctrl = new AbortController();
    cnpjAbortRef.current = ctrl;

    setCnpjStatus('buscando');
    try {
      const res = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${raw}`, { signal: ctrl.signal });
      if (!res.ok) throw new Error('não encontrado');
      const d = await res.json();

      setValue('razao_social',  d.razao_social || '');
      setValue('nome_fantasia', d.nome_fantasia || d.razao_social || '');
      setValue('setor',         d.cnae_fiscal_descricao || '');
      setValue('porte',         mapearPorte(d.descricao_porte || ''));
      setValue('cidade',        mapearCidade(d.municipio || ''));

      setCnpjStatus('ok');
      toast.success(`✅ "${d.razao_social}" preenchida automaticamente!`);
    } catch (err) {
      if (err.name === 'AbortError') return;
      setCnpjStatus('erro');
    }
  }, [setValue]);

  const handleCnpjChange = (e) => {
    const raw = apenasNumeros(e.target.value);
    const formatted = formatarCNPJ(raw);
    setCnpjDisplay(formatted);
    setValue('cnpj', formatted);
    setCnpjStatus(null);
    if (raw.length === 14) buscarCNPJ(raw);
  };

  // Reset ao trocar aba
  const handleTab = (t) => {
    setTipo(t);
    reset();
    setCnpjDisplay('');
    setCnpjStatus(null);
  };

  const onSubmit = async (data) => {
    const res = tipo === 'candidato'
      ? await cadastrarCandidato(data)
      : await cadastrarEmpresa(data);

    if (res.ok) {
      toast.success('Cadastro realizado com sucesso! 🎉');
      navigate(tipo === 'candidato' ? '/candidato' : '/empresa/pagamento');
    } else {
      toast.error(res.msg || 'Erro ao cadastrar.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-acre-dark to-[#1a4a2e] flex items-center justify-center p-4 py-10">
      <div className="w-full max-w-md">

        {/* Logo */}
        <div className="text-center mb-6">
          <Link to="/" className="inline-flex items-center gap-2">
            <span className="text-2xl">🌿</span>
            <span className="font-sora font-bold text-2xl text-white">
              Acre<span className="text-acre-gold">Job</span>
            </span>
          </Link>
          <p className="text-white/50 text-xs mt-1">Plataforma de empregos do Acre</p>
        </div>

        <div className="card overflow-hidden">
          {/* Tabs */}
          <div className="flex border-b border-gray-100">
            {[['candidato','👤 Sou Candidato'],['empresa','🏢 Sou Empresa']].map(([t, label]) => (
              <button key={t} onClick={() => handleTab(t)}
                className={`flex-1 py-3.5 text-sm font-semibold transition-colors ${
                  tipo === t
                    ? 'border-b-2 border-acre-green text-acre-green bg-acre-light/30'
                    : 'text-gray-400 hover:text-gray-600'
                }`}>
                {label}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">

            {/* ── EMPRESA: CNPJ primeiro ── */}
            {tipo === 'empresa' && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="label mb-0">CNPJ</label>
                  <span className="text-xs text-gray-400 flex items-center gap-1">
                    <Search size={10}/> dados preenchidos automaticamente
                  </span>
                </div>
                <div className="relative">
                  <input
                    className={`input pr-9 ${
                      cnpjStatus === 'ok'   ? 'border-green-400 focus:border-green-500' :
                      cnpjStatus === 'erro' ? 'border-red-400 focus:border-red-500' : ''
                    }`}
                    placeholder="00.000.000/0001-00"
                    value={cnpjDisplay}
                    onChange={handleCnpjChange}
                    maxLength={18}
                    inputMode="numeric"
                  />
                  {/* ícone de status */}
                  {cnpjStatus === 'buscando' && (
                    <Loader2 size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-500 animate-spin pointer-events-none" />
                  )}
                  {cnpjStatus === 'ok' && (
                    <CheckCircle size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500 pointer-events-none" />
                  )}
                  {cnpjStatus === 'erro' && (
                    <AlertCircle size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500 pointer-events-none" />
                  )}
                  {/* campo oculto para react-hook-form */}
                  <input type="hidden" {...register('cnpj')} />
                </div>
                {cnpjStatus === 'ok' && (
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <CheckCircle size={10}/> Dados preenchidos com informações da Receita Federal
                  </p>
                )}
                {cnpjStatus === 'erro' && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                    <AlertCircle size={10}/> CNPJ não encontrado. Preencha os dados manualmente.
                  </p>
                )}
              </div>
            )}

            {/* ── Dados comuns ── */}
            <div>
              <label className="label">{tipo === 'empresa' ? 'Nome do responsável' : 'Nome completo'}</label>
              <input className="input" placeholder="Seu nome completo"
                {...register('nome', { required: 'Nome obrigatório', minLength: { value: 3, message: 'Mínimo 3 caracteres' } })} />
              {errors.nome && <p className="text-red-500 text-xs mt-1">{errors.nome.message}</p>}
            </div>

            <div>
              <label className="label">E-mail</label>
              <input className="input" type="email" placeholder="seu@email.com"
                {...register('email', { required: 'E-mail obrigatório' })} />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
            </div>

            <div>
              <label className="label">Senha</label>
              <input className="input" type="password" placeholder="Mínimo 8 caracteres"
                {...register('senha', { required: 'Senha obrigatória', minLength: { value: 8, message: 'Mínimo 8 caracteres' } })} />
              {errors.senha && <p className="text-red-500 text-xs mt-1">{errors.senha.message}</p>}
            </div>

            {/* ── Candidato: campos extras ── */}
            {tipo === 'candidato' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">WhatsApp</label>
                  <input className="input" placeholder="68999999999"
                    {...register('whatsapp', { required: 'WhatsApp obrigatório' })} />
                  {errors.whatsapp && <p className="text-red-500 text-xs mt-1">{errors.whatsapp.message}</p>}
                </div>
                <div>
                  <label className="label">Cidade</label>
                  <select className="input" {...register('cidade', { required: 'Cidade obrigatória' })}>
                    <option value="">Selecione...</option>
                    {MUNICIPIOS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                  {errors.cidade && <p className="text-red-500 text-xs mt-1">{errors.cidade.message}</p>}
                </div>
              </div>
            )}

            {/* ── Empresa: campos extras ── */}
            {tipo === 'empresa' && (
              <>
                <div>
                  <label className="label">Razão Social</label>
                  <input className="input" placeholder="Nome oficial da empresa"
                    {...register('razao_social', { required: 'Razão social obrigatória' })} />
                  {errors.razao_social && <p className="text-red-500 text-xs mt-1">{errors.razao_social.message}</p>}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label">Nome Fantasia</label>
                    <input className="input" placeholder="Como é conhecida"
                      {...register('nome_fantasia')} />
                  </div>
                  <div>
                    <label className="label">Cidade</label>
                    <select className="input" {...register('cidade')}>
                      <option value="">Selecione...</option>
                      {MUNICIPIOS.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label">Setor / Ramo</label>
                    <input className="input" placeholder="Ex: Comércio varejista"
                      {...register('setor')} />
                  </div>
                  <div>
                    <label className="label">Porte</label>
                    <select className="input" {...register('porte')}>
                      <option value="">Selecione...</option>
                      {['mei','micro','pequena','media','grande'].map(p => (
                        <option key={p} value={p}>{p.charAt(0).toUpperCase()+p.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700 flex items-start gap-2">
                  <span className="text-base flex-shrink-0">💳</span>
                  <span>Após o cadastro você será redirecionado para realizar o pagamento e liberar o acesso completo.</span>
                </div>
              </>
            )}

            {/* ── Aceite dos termos ── */}
            <div>
              <label className="flex items-start gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="mt-0.5 accent-acre-green w-4 h-4 flex-shrink-0"
                  {...register('aceite_termos', { required: 'Você deve aceitar os termos para continuar.' })}
                />
                <span className="text-xs text-gray-600 leading-relaxed">
                  Li e aceito os{' '}
                  <Link to="/termos-uso" target="_blank" className="text-acre-green font-semibold hover:underline">
                    Termos de Uso
                  </Link>
                  {' '}e a{' '}
                  <Link to="/politica-privacidade" target="_blank" className="text-acre-green font-semibold hover:underline">
                    Política de Privacidade
                  </Link>
                </span>
              </label>
              {errors.aceite_termos && (
                <p className="text-red-500 text-xs mt-1">{errors.aceite_termos.message}</p>
              )}
            </div>

            <button type="submit" disabled={loading}
              className={`w-full py-3 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 disabled:opacity-60 ${
                tipo === 'candidato' ? 'btn-primary' : 'btn-gold'
              }`}>
              {loading
                ? <><Loader2 size={15} className="animate-spin" /> Criando conta...</>
                : tipo === 'candidato' ? '🚀 Criar conta grátis' : '🏢 Cadastrar empresa'
              }
            </button>
          </form>

          <div className="px-6 pb-5 text-center">
            <p className="text-sm text-gray-500">
              Já tem conta?{' '}
              <Link to="/login" className="text-acre-green font-semibold hover:underline">Entrar</Link>
            </p>
          </div>
        </div>

        <p className="text-center text-white/40 text-xs mt-5">
          <Link to="/" className="hover:text-white/70">← Voltar ao início</Link>
        </p>
      </div>
    </div>
  );
}
